import React, { useState } from "react";
import { useHSE } from "../lib/store";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../components/ui/dialog";
import { Save, Plus, Pencil, Trash2, Search, AlertTriangle, Building2, FileText, Tag, Users, Shield, Target, Lock, DatabaseBackup, Download, Upload } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const { data, setData } = useHSE();
  const [tab, setTab] = useState("company");

  const tabs = [
    { id: "company", label: "Company", icon: Shield },
    { id: "security", label: "Login & Security", icon: Lock },
    { id: "backup", label: "Backup / Restore", icon: DatabaseBackup },
    { id: "sites", label: "Site Management", icon: Building2 },
    { id: "legal", label: "Legal References", icon: FileText },
    { id: "regions", label: "Regions", icon: Tag },
    { id: "departments", label: "Departments", icon: Users },
    { id: "contractors", label: "Contractors", icon: Tag },
    { id: "inspectionTypes", label: "Inspection Types", icon: Tag },
    { id: "incidentTypes", label: "Incident Types", icon: Tag },
    { id: "obsCategories", label: "Observation Categories", icon: Tag },
    { id: "observationSubcategories", label: "Observation Subcategories", icon: Tag },
    { id: "sorCategories", label: "SOR Categories", icon: Tag },
    { id: "ncrCategories", label: "NCR Categories", icon: Tag },
    { id: "actionOwners", label: "Action Owners", icon: Users },
    { id: "mandateRecipients", label: "Mandate Recipients", icon: Users },
    { id: "trainingTypes", label: "Training Types", icon: Tag },
    { id: "ptwTypes", label: "PTW Types", icon: Tag },
    { id: "auditTypes", label: "Audit Types", icon: Tag },
    { id: "riskLevels", label: "Risk Levels", icon: Tag },
    { id: "statusValues", label: "Status Values", icon: Tag },
    { id: "targets", label: "HSE Targets", icon: Target },
    { id: "users", label: "Users", icon: Users },
  ];

  return (
    <div className="space-y-5" data-testid="settings-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight text-[#082B4D]">Settings & Master Data</h1>
          <div className="text-sm text-slate-500">Manage company, sites, legal references and all master data</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-5">
        {/* Tab navigation */}
        <div className="command-card p-2 h-fit lg:sticky lg:top-32">
          <nav className="flex flex-col gap-0.5">
            {tabs.map((t) => (
              <button
                key={t.id}
                data-testid={`settings-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2.5 px-3 py-2 rounded-md text-[12px] font-medium transition ${
                  tab === t.id ? "bg-[#082B4D] text-white" : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                <t.icon className="w-3.5 h-3.5 shrink-0" />
                {t.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="space-y-5">
          {tab === "company" && <CompanyTab data={data} setData={setData} />}
          {tab === "security" && <LoginSecurityTab />}
          {tab === "backup" && <BackupRestoreTab data={data} setData={setData} />}
          {tab === "sites" && <SiteManagement data={data} setData={setData} />}
          {tab === "legal" && <LegalReferencesTab data={data} setData={setData} />}
          {tab === "regions" && (
            <SimpleListTab key="regions" data={data} setData={setData} listKey="regions" title="Regions" />
          )}
          {tab === "targets" && <TargetsTab data={data} setData={setData} />}
          {tab === "users" && <UsersTab data={data} />}
          {[
            "departments", "contractors", "inspectionTypes", "incidentTypes",
            "obsCategories", "observationSubcategories", "sorCategories", "ncrCategories", "actionOwners", "mandateRecipients",
            "trainingTypes", "ptwTypes", "auditTypes", "riskLevels", "statusValues"
          ].includes(tab) && (
            <SimpleListTab key={tab} data={data} setData={setData} listKey={tab} title={tabs.find(x => x.id === tab).label} />
          )}
        </div>
      </div>
    </div>
  );
}

function CompanyTab({ data, setData }) {
  const [s, setS] = useState(data.settings);
  const save = () => {
    setData({ ...data, settings: s });
    toast.success("Company settings saved");
  };
  return (
    <div className="command-card p-5">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="font-heading font-bold text-base text-[#082B4D]">Company / Client</h3>
        <Button onClick={save} className="gap-1.5 text-white" style={{ background: "#18B84F" }} data-testid="settings-save">
          <Save className="w-4 h-4" /> Save
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Client Name" value={s.client} onChange={(v) => setS({ ...s, client: v })} testid="settings-client" />
        <Field label="System Name" value={s.company} onChange={(v) => setS({ ...s, company: v })} testid="settings-company" />
        <Field label="System Title" value={s.systemTitle || "HSSE Command & Reporting System"} onChange={(v) => setS({ ...s, systemTitle: v })} testid="settings-system-title" />
        <Field label="Address" value={s.address || ""} onChange={(v) => setS({ ...s, address: v })} testid="settings-address" />
        <Field label="Contact Person" value={s.contactPerson || ""} onChange={(v) => setS({ ...s, contactPerson: v })} testid="settings-contact-person" />
        <Field label="Email" value={s.email || ""} onChange={(v) => setS({ ...s, email: v })} testid="settings-email" />
        <Field label="Phone" value={s.phone || ""} onChange={(v) => setS({ ...s, phone: v })} testid="settings-phone" />
        <Field label="Report Header Text" value={s.reportHeader || ""} onChange={(v) => setS({ ...s, reportHeader: v })} testid="settings-report-header" />
        <Field label="Report Footer Text" value={s.reportFooter || ""} onChange={(v) => setS({ ...s, reportFooter: v })} testid="settings-report-footer" />
        <Field label="Theme Color" value={s.themeColor} onChange={(v) => setS({ ...s, themeColor: v })} testid="settings-theme" />
      </div>
      <div className="mt-4 p-3 rounded-lg bg-emerald-50 border border-emerald-100 text-[11px] text-emerald-800">
        <div className="font-semibold mb-1">Frontend data saved</div>
        Every change you make is saved in the current frontend workspace and remains available after refresh.
      </div>
    </div>
  );
}

function LoginSecurityTab() {
  const { creds, updateCredentials, session } = useAuth();
  const [cur, setCur] = useState("");
  const [u, setU] = useState(creds.username);
  const [d, setD] = useState(creds.displayName || "");
  const [p, setP] = useState("");
  const [pc, setPc] = useState("");
  const [busy, setBusy] = useState(false);

  const onSave = async () => {
    if (p && p !== pc) { toast.error("New password and confirmation do not match"); return; }
    setBusy(true);
    const res = await updateCredentials({ currentPassword: cur, newUsername: u, newPassword: p || undefined, displayName: d });
    setBusy(false);
    if (!res.ok) { toast.error(res.error); return; }
    toast.success("Credentials updated. Use the new login on next sign-in.");
    setCur(""); setP(""); setPc("");
  };

  return (
    <div className="command-card p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-heading font-bold text-base text-[#082B4D]">Login & Security</h3>
          <div className="text-[12px] text-slate-500">Update administrator credentials and display name</div>
        </div>
        <Button onClick={onSave} disabled={!cur || busy} className="gap-1.5 text-white" style={{ background: "#18B84F" }} data-testid="security-save">
          <Save className="w-4 h-4" /> Update Credentials
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Current Password (required)" type="password" value={cur} onChange={setCur} testid="security-current-password" />
        <Field label="Display Name" value={d} onChange={setD} testid="security-display-name" />
        <Field label="New Username" value={u} onChange={setU} testid="security-username" />
        <div />
        <Field label="New Password (leave blank to keep current)" type="password" value={p} onChange={setP} testid="security-new-password" />
        <Field label="Confirm New Password" type="password" value={pc} onChange={setPc} testid="security-confirm-password" />
      </div>
      <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-100 text-[11px] text-amber-800">
        Currently signed in as <span className="font-mono font-semibold">@{session?.user?.username}</span>. After changing credentials, the new login applies the next time you sign in.
      </div>
    </div>
  );
}


function BackupRestoreTab({ data, setData }) {
  const exportBackup = () => {
    const payload = {
      app: "HSSE Command & Reporting System",
      exportedAt: new Date().toISOString(),
      schemaVersion: data.schemaVersion,
      data,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const client = (data.settings?.client || "client").replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase();
    a.href = url;
    a.download = `hsse-command-reporting-backup-${client}-${new Date().toISOString().slice(0,10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Full system backup exported");
  };

  const importBackup = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result);
        const nextData = parsed.data || parsed;
        if (!nextData || !nextData.schemaVersion || !nextData.settings) {
          toast.error("Invalid backup file");
          return;
        }
        if (!confirm("This will replace the current dashboard data in this browser/session. Continue?")) return;
        setData(nextData);
        toast.success("Backup restored successfully");
      } catch {
        toast.error("Could not read backup file");
      }
    };
    reader.readAsText(file);
  };

  return (
    <div className="command-card p-5">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div>
          <h3 className="font-heading font-bold text-base text-[#082B4D]">Backup / Restore</h3>
          <div className="text-[12px] text-slate-500">Export a full JSON backup before changing settings, forms or master data.</div>
        </div>
        <Button onClick={exportBackup} className="gap-1.5 text-white" style={{ background: "#082B4D" }} data-testid="backup-export">
          <Download className="w-4 h-4" /> Export Full Backup
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <div className="font-semibold text-sm text-slate-700 mb-1">What is included?</div>
          <ul className="text-[12px] text-slate-500 space-y-1 list-disc pl-4">
            <li>Client/company settings and branding text</li>
            <li>Sites, departments, legal references and master lists</li>
            <li>Incidents, inspections, PTW, trainings, observations, NCR, audits and waste data</li>
            <li>Monthly KPI sheet data and HSSE targets</li>
          </ul>
        </div>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <div className="font-semibold text-sm text-amber-800 mb-2">Restore from backup</div>
          <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-white border border-amber-200 text-amber-800 text-xs font-semibold cursor-pointer hover:bg-amber-100">
            <Upload className="w-4 h-4" /> Select JSON Backup
            <input type="file" accept="application/json,.json" className="hidden" onChange={(e) => importBackup(e.target.files?.[0])} data-testid="backup-import" />
          </label>
          <div className="text-[11px] text-amber-700 mt-3">Restore only trusted backup files. Import will overwrite the current dashboard data.</div>
        </div>
      </div>
    </div>
  );
}

function TargetsTab({ data, setData }) {
  const [t, setT] = useState(data.settings.targets);
  const save = () => {
    setData({ ...data, settings: { ...data.settings, targets: t } });
    toast.success("Targets saved");
  };
  return (
    <div className="command-card p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-bold text-base text-[#082B4D]">HSE Targets</h3>
        <Button onClick={save} className="gap-1.5 text-white" style={{ background: "#18B84F" }} data-testid="targets-save">
          <Save className="w-4 h-4" /> Save Targets
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        <Field label="Monthly Incident Target" type="number" value={t.incidentTarget || 0} onChange={(v) => setT({ ...t, ltif: Number(v) })} testid="target-ltif" />
        <Field label="Training Compliance %" type="number" value={t.trainingCompliance} onChange={(v) => setT({ ...t, trainingCompliance: Number(v) })} testid="target-training" />
        <Field label="Observation Closure %" type="number" value={t.obsClosure} onChange={(v) => setT({ ...t, obsClosure: Number(v) })} testid="target-obs" />
        <Field label="NCR Closure %" type="number" value={t.ncrClosure} onChange={(v) => setT({ ...t, ncrClosure: Number(v) })} testid="target-ncr" />
        <Field label="SOR Closure %" type="number" value={t.sorClosure} onChange={(v) => setT({ ...t, sorClosure: Number(v) })} testid="target-sor" />
      </div>
    </div>
  );
}

function UsersTab({ data }) {
  return (
    <div className="command-card p-5">
      <h3 className="font-heading font-bold text-base text-[#082B4D] mb-3">Users</h3>
      <table className="w-full text-[12px]">
        <thead className="bg-slate-50">
          <tr>
            <th className="text-left py-2 px-3 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">ID</th>
            <th className="text-left py-2 px-3 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Name</th>
            <th className="text-left py-2 px-3 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Role</th>
          </tr>
        </thead>
        <tbody>
          {data.settings.users.map((u) => (
            <tr key={u.id} className="border-t border-slate-100">
              <td className="py-2 px-3 font-mono text-slate-500">{u.id}</td>
              <td className="py-2 px-3 text-slate-700">{u.name}</td>
              <td className="py-2 px-3 text-slate-700">{u.role}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------- Site Management ----------
function SiteManagement({ data, setData }) {
  const REGIONS = data.settings.regions || ["North", "South", "East", "West", "Central"];
  const sites = data.settings.sites || [];
  const [search, setSearch] = useState("");
  const [region, setRegion] = useState("All");
  const [status, setStatus] = useState("All");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const filtered = sites.filter((s) => {
    if (search && !(`${s.number} ${s.name} ${s.code} ${s.location}`.toLowerCase().includes(search.toLowerCase()))) return false;
    if (region !== "All" && s.region !== region) return false;
    if (status !== "All" && s.status !== status) return false;
    return true;
  });

  const addNew = () => {
    const next = sites.length + 1;
    const num = String(next).padStart(3, "0");
    setEditing({
      id: `SITE-${num}`,
      number: `Site ${num}`,
      name: `Site ${num}`,
      code: `S${num}`,
      region: "North",
      location: "",
      latitude: 0,
      longitude: 0,
      manager: "",
      contact: "",
      status: "Active",
      createdDate: new Date().toISOString().slice(0, 10),
      updatedDate: new Date().toISOString().slice(0, 10),
    });
    setOpen(true);
  };

  const save = () => {
    const exists = sites.find((s) => s.id === editing.id);
    const next = exists
      ? sites.map((s) => (s.id === editing.id ? { ...editing, updatedDate: new Date().toISOString().slice(0, 10) } : s))
      : [...sites, editing];
    setData({ ...data, settings: { ...data.settings, sites: next } });
    setOpen(false);
    toast.success(exists ? "Site updated" : "Site added");
  };

  const remove = (s) => {
    const usedIn = countUsage(data, s.number);
    const msg = usedIn > 0
      ? `⚠️ This site is used in ${usedIn} record(s). Delete anyway?`
      : "Delete this site?";
    if (!confirm(msg)) return;
    setData({ ...data, settings: { ...data.settings, sites: sites.filter((x) => x.id !== s.id) } });
    toast.success("Site deleted");
  };

  const toggleStatus = (s) => {
    const next = sites.map((x) => x.id === s.id ? { ...x, status: x.status === "Active" ? "Inactive" : "Active", updatedDate: new Date().toISOString().slice(0, 10) } : x);
    setData({ ...data, settings: { ...data.settings, sites: next } });
    toast.success(`Site ${s.status === "Active" ? "deactivated" : "activated"}`);
  };

  return (
    <div className="space-y-4">
      <div className="command-card p-4 flex items-center gap-2 flex-wrap">
        <h3 className="font-heading font-bold text-base text-[#082B4D] mr-auto">Site Management ({sites.length} sites)</h3>
        <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5">
          <Search className="w-4 h-4 text-slate-400" />
          <input data-testid="sites-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search sites…" className="text-xs bg-transparent focus:outline-none w-44" />
        </div>
        <select value={region} onChange={(e) => setRegion(e.target.value)} className="border border-slate-200 rounded-md px-2.5 py-1.5 text-sm bg-white" data-testid="sites-region-filter">
          <option>All</option>{REGIONS.map((r) => <option key={r}>{r}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-slate-200 rounded-md px-2.5 py-1.5 text-sm bg-white" data-testid="sites-status-filter">
          <option>All</option><option>Active</option><option>Inactive</option>
        </select>
        <Button onClick={addNew} className="gap-1.5 text-white" style={{ background: "#082B4D" }} data-testid="sites-add">
          <Plus className="w-4 h-4" /> Add Site
        </Button>
      </div>

      <div className="command-card overflow-hidden">
        <div className="overflow-x-auto" style={{ maxHeight: "60vh" }}>
          <table className="w-full text-[12px]">
            <thead style={{ background: "#082B4D", position: "sticky", top: 0, zIndex: 10 }}>
              <tr>
                {["Site ID", "Number", "Name", "Code", "Region", "Location", "Manager", "Contact", "Status", "Updated", "Actions"].map((h) => (
                  <th key={h} className="text-left py-2.5 px-3 text-[10px] uppercase tracking-wider text-white font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((s, i) => (
                <tr key={s.id} className={`border-b border-slate-100 hover:bg-slate-50 ${i % 2 ? "bg-slate-50/50" : ""}`}>
                  <td className="py-2 px-3 font-mono text-[11px] text-slate-500">{s.id}</td>
                  <td className="py-2 px-3 font-semibold text-[#082B4D]">{s.number}</td>
                  <td className="py-2 px-3 text-slate-700">{s.name}</td>
                  <td className="py-2 px-3 font-mono text-[11px] text-slate-600">{s.code}</td>
                  <td className="py-2 px-3 text-slate-700">{s.region}</td>
                  <td className="py-2 px-3 text-slate-600">{s.location}</td>
                  <td className="py-2 px-3 text-slate-600">{s.manager}</td>
                  <td className="py-2 px-3 text-slate-500 font-mono text-[11px]">{s.contact}</td>
                  <td className="py-2 px-3">
                    <span className="cmd-pill" style={s.status === "Active" ? { background: "#DCFCE7", color: "#166534" } : { background: "#F3F4F6", color: "#6B7280" }}>
                      {s.status}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-slate-500 text-[11px]">{s.updatedDate}</td>
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-1">
                      <button data-testid={`site-toggle-${s.id}`} className="text-[10px] uppercase tracking-wider px-2 py-1 rounded border border-slate-200 hover:bg-slate-50" onClick={() => toggleStatus(s)}>
                        {s.status === "Active" ? "Deactivate" : "Activate"}
                      </button>
                      <button data-testid={`site-edit-${s.id}`} className="text-slate-500 hover:text-[#082B4D] p-1" onClick={() => { setEditing(s); setOpen(true); }}>
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button data-testid={`site-delete-${s.id}`} className="text-slate-500 hover:text-rose-500 p-1" onClick={() => remove(s)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2.5 border-t border-slate-100 text-[11px] text-slate-500">
          {filtered.length} of {sites.length} sites
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading">{sites.find(x => x.id === editing?.id) ? "Edit Site" : "Add New Site"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1">
              <Field label="Site ID" value={editing.id} onChange={(v) => setEditing({ ...editing, id: v })} testid="field-site-id" />
              <Field label="Site Number" value={editing.number} onChange={(v) => setEditing({ ...editing, number: v })} testid="field-site-number" />
              <Field label="Site Name / Description" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testid="field-site-name" />
              <Field label="Site Code" value={editing.code} onChange={(v) => setEditing({ ...editing, code: v })} testid="field-site-code" />
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Region / Zone</label>
                <select value={editing.region} onChange={(e) => setEditing({ ...editing, region: e.target.value })} data-testid="field-site-region" className="mt-1 w-full border border-slate-200 rounded-md px-2.5 py-1.5 text-sm bg-white">
                  {REGIONS.map((r) => <option key={r}>{r}</option>)}
                </select>
              </div>
              <Field label="Location" value={editing.location} onChange={(v) => setEditing({ ...editing, location: v })} testid="field-site-location" />
              <Field label="GPS Latitude" type="number" value={editing.latitude} onChange={(v) => setEditing({ ...editing, latitude: Number(v) })} testid="field-site-lat" />
              <Field label="GPS Longitude" type="number" value={editing.longitude} onChange={(v) => setEditing({ ...editing, longitude: Number(v) })} testid="field-site-long" />
              <Field label="Site Manager" value={editing.manager} onChange={(v) => setEditing({ ...editing, manager: v })} testid="field-site-manager" />
              <Field label="Contact Number" value={editing.contact} onChange={(v) => setEditing({ ...editing, contact: v })} testid="field-site-contact" />
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Status</label>
                <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} data-testid="field-site-status" className="mt-1 w-full border border-slate-200 rounded-md px-2.5 py-1.5 text-sm bg-white">
                  <option>Active</option><option>Inactive</option>
                </select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="text-white" style={{ background: "#082B4D" }} data-testid="site-dialog-save">Save Site</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function countUsage(data, siteName) {
  return ["incidents","inspections","actions","ptws","trainings","observations","ncrs","audits","waste"]
    .reduce((acc, k) => acc + (data[k] || []).filter((r) => r.site === siteName).length, 0);
}

// ---------- Legal References ----------
function LegalReferencesTab({ data, setData }) {
  const refs = data.legalRefs || [];
  const [search, setSearch] = useState("");
  const [moduleFilter, setModuleFilter] = useState("All");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const filtered = refs.filter((r) => {
    if (search && !(`${r.id} ${r.title} ${r.regulation} ${r.clause}`.toLowerCase().includes(search.toLowerCase()))) return false;
    if (moduleFilter !== "All" && r.module !== moduleFilter) return false;
    return true;
  });

  const addNew = () => {
    const next = `LR-${String(refs.length + 1).padStart(3, "0")}`;
    setEditing({ id: next, title: "", regulation: "", clause: "", description: "", module: "All", status: "Active" });
    setOpen(true);
  };

  const save = () => {
    const exists = refs.find((r) => r.id === editing.id);
    const list = exists ? refs.map((r) => r.id === editing.id ? editing : r) : [editing, ...refs];
    setData({ ...data, legalRefs: list });
    setOpen(false);
    toast.success(exists ? "Legal reference updated" : "Legal reference added");
  };

  const remove = (r) => {
    const usedInsp = data.inspections.filter((x) => x.legalRef === r.id).length;
    const usedAct = data.actions.filter((x) => x.legalRef === r.id).length;
    const used = usedInsp + usedAct;
    if (!confirm(used > 0 ? `⚠️ Used in ${used} record(s). Delete anyway?` : "Delete this legal reference?")) return;
    setData({ ...data, legalRefs: refs.filter((x) => x.id !== r.id) });
    toast.success("Legal reference deleted");
  };

  return (
    <div className="space-y-4">
      <div className="command-card p-4 flex items-center gap-2 flex-wrap">
        <h3 className="font-heading font-bold text-base text-[#082B4D] mr-auto">Legal References ({refs.length})</h3>
        <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5">
          <Search className="w-4 h-4 text-slate-400" />
          <input data-testid="legal-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className="text-xs bg-transparent focus:outline-none w-44" />
        </div>
        <select value={moduleFilter} onChange={(e) => setModuleFilter(e.target.value)} className="border border-slate-200 rounded-md px-2.5 py-1.5 text-sm bg-white" data-testid="legal-module-filter">
          <option>All</option><option>Inspection</option><option>Action Plan</option><option>Incident</option><option>NCR</option><option>SOR</option>
        </select>
        <Button onClick={addNew} className="gap-1.5 text-white" style={{ background: "#082B4D" }} data-testid="legal-add">
          <Plus className="w-4 h-4" /> Add Legal Reference
        </Button>
      </div>

      <div className="command-card overflow-hidden">
        <table className="w-full text-[12px]">
          <thead style={{ background: "#082B4D" }}>
            <tr>{["Reference ID", "Title", "Regulation", "Clause", "Module", "Status", "Actions"].map((h) => (
              <th key={h} className="text-left py-2.5 px-3 text-[10px] uppercase tracking-wider text-white font-semibold whitespace-nowrap">{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => (
              <tr key={r.id} className={`border-b border-slate-100 hover:bg-slate-50 ${i % 2 ? "bg-slate-50/50" : ""}`}>
                <td className="py-2 px-3 font-mono text-slate-500">{r.id}</td>
                <td className="py-2 px-3 text-slate-700 font-medium">{r.title}</td>
                <td className="py-2 px-3 text-slate-600">{r.regulation}</td>
                <td className="py-2 px-3 font-mono text-[11px] text-slate-600">{r.clause}</td>
                <td className="py-2 px-3"><span className="cmd-pill" style={{ background: "#DBEAFE", color: "#1E40AF" }}>{r.module}</span></td>
                <td className="py-2 px-3"><span className="cmd-pill" style={r.status === "Active" ? { background: "#DCFCE7", color: "#166534" } : { background: "#F3F4F6", color: "#6B7280" }}>{r.status}</span></td>
                <td className="py-2 px-3">
                  <button data-testid={`legal-edit-${r.id}`} className="text-slate-500 hover:text-[#082B4D] p-1" onClick={() => { setEditing(r); setOpen(true); }}>
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button data-testid={`legal-delete-${r.id}`} className="text-slate-500 hover:text-rose-500 p-1" onClick={() => remove(r)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading">Legal Reference</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Reference ID" value={editing.id} onChange={(v) => setEditing({ ...editing, id: v })} />
              <Field label="Reference Title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} />
              <Field label="Regulation / Standard" value={editing.regulation} onChange={(v) => setEditing({ ...editing, regulation: v })} />
              <Field label="Clause Number" value={editing.clause} onChange={(v) => setEditing({ ...editing, clause: v })} />
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Description</label>
                <textarea rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  className="mt-1 w-full border border-slate-200 rounded-md px-2.5 py-1.5 text-sm" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Applicable Module</label>
                <select value={editing.module} onChange={(e) => setEditing({ ...editing, module: e.target.value })}
                  className="mt-1 w-full border border-slate-200 rounded-md px-2.5 py-1.5 text-sm bg-white">
                  {["Inspection", "Action Plan", "Incident", "NCR", "SOR", "All"].map((m) => <option key={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Status</label>
                <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}
                  className="mt-1 w-full border border-slate-200 rounded-md px-2.5 py-1.5 text-sm bg-white">
                  <option>Active</option><option>Inactive</option>
                </select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="text-white" style={{ background: "#082B4D" }} data-testid="legal-dialog-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------- Simple list editor (string arrays) ----------
function SimpleListTab({ data, setData, listKey, title }) {
  const list = data.settings[listKey] || [];
  const [search, setSearch] = useState("");
  const [val, setVal] = useState("");

  const usageOf = (item) => {
    return ["incidents","inspections","actions","ptws","trainings","observations","ncrs","audits","waste"]
      .reduce((acc, k) => acc + (data[k] || []).filter((r) => r.department === item || r.type === item || r.category === item || r.contractor === item).length, 0);
  };

  const add = () => {
    if (!val.trim()) return;
    if (list.includes(val.trim())) { toast.error("Already exists"); return; }
    const nextValue = val.trim();
    setData((current) => {
      const currentList = current.settings?.[listKey] || [];
      if (currentList.includes(nextValue)) return current;
      return {
        ...current,
        settings: { ...current.settings, [listKey]: [...currentList, nextValue] },
      };
    });
    setVal("");
    toast.success(`${title} added`);
  };

  const remove = (i) => {
    const item = list[i];
    const used = usageOf(item);
    if (!confirm(used > 0 ? `⚠️ "${item}" may be used in records. Delete anyway?` : `Delete "${item}"?`)) return;
    setData((current) => ({
      ...current,
      settings: {
        ...current.settings,
        [listKey]: (current.settings?.[listKey] || []).filter((_, x) => x !== i),
      },
    }));
    toast.success("Deleted");
  };

  const edit = (i) => {
    const cur = list[i];
    const next = prompt(`Edit ${title.toLowerCase()}:`, cur);
    if (next === null || next.trim() === "") return;
    if (list.includes(next.trim()) && next.trim() !== cur) { toast.error("Already exists"); return; }
    setData((current) => {
      const updated = [...(current.settings?.[listKey] || [])];
      updated[i] = next.trim();
      return { ...current, settings: { ...current.settings, [listKey]: updated } };
    });
    toast.success("Updated");
  };

  const filtered = list
    .map((value, index) => ({ value, index }))
    .filter(({ value }) => value.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="command-card p-5">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="font-heading font-bold text-base text-[#082B4D]">{title} ({list.length})</h3>
        <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5">
          <Search className="w-4 h-4 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className="text-xs bg-transparent focus:outline-none w-44" data-testid={`${listKey}-search`} />
        </div>
      </div>
      <div className="flex gap-2 mb-3">
        <Input data-testid={`${listKey}-input`} value={val} onChange={(e) => setVal(e.target.value)} placeholder={`Add ${title.toLowerCase()}…`} className="h-9 text-sm flex-1" />
        <Button onClick={add} className="gap-1.5 text-white" style={{ background: "#082B4D" }} data-testid={`${listKey}-add`}>
          <Plus className="w-4 h-4" /> Add
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[50vh] overflow-y-auto">
        {filtered.map(({ value, index }) => (
          <div key={`${value}-${index}`} className="flex items-center justify-between bg-slate-50 rounded px-3 py-2 text-[12px] border border-slate-100">
            <span className="text-slate-700 font-medium">{value}</span>
            <div className="flex gap-1">
              <button onClick={() => edit(index)} className="text-slate-500 hover:text-[#082B4D] p-1"><Pencil className="w-3.5 h-3.5" /></button>
              <button onClick={() => remove(index)} className="text-slate-500 hover:text-rose-500 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="text-slate-400 italic text-sm py-4 col-span-2 text-center">No items</div>}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text" }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</label>
      <Input data-testid={testid} type={type} value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="mt-1 h-9 text-sm" />
    </div>
  );
}
